import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect } from "react";
import { ChevronLeft, Pencil, Trash2, Share2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { AppShell } from "@/components/app-shell";
import { ImageBox } from "@/components/ui-extras";
import { StarRating } from "@/components/star-rating";
import { Dialog } from "@/components/ui/dialog";
import { LookEditDialog } from "@/components/look-edit-dialog";
import { useAuth } from "@/hooks/use-auth";
import { type MoodBoardItem } from "@/components/item-detail-sections";
import { CaileveOrbitBoard } from "@/components/caileve-orbit-board";
import { useShareCaileve } from "@/hooks/use-share-caileve";
import { bgFields } from "@/lib/photo-preference";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

type LookKind = "jewelry" | "clothing" | "accessory";
type LookItemPiece = {
  id: string;
  name: string;
  photo_url: string | null;
  photo_url_no_bg: string | null;
  prefer_clean_bg: boolean | null;
  clean_bg_color: string | null;
  caption: string | null;
  kind: LookKind;
  missing?: boolean;
};
type LookDetail = {
  id: string;
  name: string;
  occasion: string | null;
  rating: number | null;
  look_photo_url: string | null;
  styling_notes: string | null;
  is_favorite: boolean | null;
  feelings: string | null;
  mood: string | null;
  pieces: LookItemPiece[];
};

export const Route = createFileRoute("/looks/$id")({
  head: () => ({
    meta: [
      { title: "Look — Cailève" },
      { name: "description", content: "A saved editorial look from your collection." },
    ],
  }),
  component: () => (
    <AppShell>
      <LookDetailPage />
    </AppShell>
  ),
});

function LookDetailPage() {
  const { id } = Route.useParams();
  const { user } = useAuth();
  if (!user) return null;
  const uid = user.id;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const share = useShareCaileve();
  const [showShareNudge, setShowShareNudge] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const shown = sessionStorage.getItem("caileve_look_share_shown");
    if (!shown) {
      setShowShareNudge(true);
      sessionStorage.setItem("caileve_look_share_shown", "1");
    }
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["saved_look_detail", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("saved_looks")
        .select(
          `id, name, occasion, rating, look_photo_url, styling_notes, is_favorite, feelings, mood,
           saved_look_items (
             id, item_type, item_id, clothing_item_id, jewelry_item_id, position
           )`
        )
        .eq("id", id)
        .maybeSingle();
      if (error) throw new Error(error.message || "Network error");
      if (!data) return null;
      const row = data as unknown as {
        id: string;
        name: string;
        occasion: string | null;
        rating: number | null;
        look_photo_url: string | null;
        styling_notes: string | null;
        is_favorite: boolean | null;
        feelings: string | null;
        mood: string | null;
        saved_look_items: {
          id: string;
          item_type: string | null;
          item_id: string | null;
          clothing_item_id: string | null;
          jewelry_item_id: string | null;
          position: number | null;
        }[];
      };
      // Resolve each junction row to {kind, id} using new fields first, legacy mirrors otherwise.
      const resolved = row.saved_look_items
        .map((r) => {
          const itemId = r.item_id ?? r.clothing_item_id ?? r.jewelry_item_id;
          if (!itemId) return null;
          let kind: LookKind | null = null;
          if (r.item_type === "clothing" || r.item_type === "jewelry" || r.item_type === "accessory") {
            kind = r.item_type as LookKind;
          } else if (r.clothing_item_id) {
            kind = "clothing";
          } else if (r.jewelry_item_id) {
            kind = "jewelry"; // may be re-classified as accessory below via is_accessory
          }
          if (!kind) return null;
          return { id: itemId, kind, position: r.position ?? 0 };
        })
        .filter((x): x is { id: string; kind: LookKind; position: number } => !!x);

      const clothingIds = Array.from(new Set(resolved.filter((r) => r.kind === "clothing").map((r) => r.id)));
      const jewelryishIds = Array.from(
        new Set(resolved.filter((r) => r.kind !== "clothing").map((r) => r.id)),
      );
      const [{ data: cRows }, { data: jRows }] = await Promise.all([
        clothingIds.length
          ? supabase
              .from("clothing_items")
              .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color, clothing_type")
              .in("id", clothingIds)
          : Promise.resolve({ data: [] as Array<{ id: string; name: string; photo_url: string | null; photo_url_no_bg: string | null; prefer_clean_bg: boolean | null; clean_bg_color: string | null; clothing_type: string | null }> }),
        jewelryishIds.length
          ? supabase
              .from("jewelry_items")
              .select("id, name, photo_url, photo_url_no_bg, prefer_clean_bg, clean_bg_color, category, accessory_type, is_accessory")
              .in("id", jewelryishIds)
          : Promise.resolve({ data: [] as Array<{ id: string; name: string; photo_url: string | null; photo_url_no_bg: string | null; prefer_clean_bg: boolean | null; clean_bg_color: string | null; category: string | null; accessory_type: string | null; is_accessory: boolean | null }> }),
      ]);
      const cMap = new Map((cRows ?? []).map((r) => [r.id, r]));
      const jMap = new Map((jRows ?? []).map((r) => [r.id, r]));

      const pieces: LookItemPiece[] = resolved
        .sort((a, b) => a.position - b.position)
        .map((r) => {
          if (r.kind === "clothing") {
            const m = cMap.get(r.id);
            if (!m) {
              return {
                id: r.id,
                name: "Removed piece",
                photo_url: null,
                photo_url_no_bg: null,
                prefer_clean_bg: null,
                clean_bg_color: null,
                caption: null,
                kind: "clothing" as const,
                missing: true,
              };
            }
            return { id: m.id, name: m.name, photo_url: m.photo_url, ...bgFields(m), caption: m.clothing_type, kind: "clothing" as const };
          }
          const m = jMap.get(r.id);
          if (!m) {
            const kind: LookKind = r.kind === "accessory" ? "accessory" : "jewelry";
            return {
              id: r.id,
              name: "Removed piece",
              photo_url: null,
              photo_url_no_bg: null,
              prefer_clean_bg: null,
              clean_bg_color: null,
              caption: null,
              kind,
              missing: true,
            };
          }
          // Reclassify legacy jewelry rows that actually point at accessories.
          const kind: LookKind =
            r.kind === "accessory" || m.is_accessory ? "accessory" : "jewelry";
          const caption = kind === "accessory" ? m.accessory_type ?? m.category : m.category;
          return { id: m.id, name: m.name, photo_url: m.photo_url, ...bgFields(m), caption, kind };
        });

      return {
        id: row.id,
        name: row.name,
        occasion: row.occasion,
        rating: row.rating,
        look_photo_url: row.look_photo_url,
        styling_notes: row.styling_notes,
        is_favorite: row.is_favorite,
        feelings: row.feelings,
        mood: row.mood,
        pieces,
      } as LookDetail;
    },
  });

  const del = useMutation({
    mutationFn: async () => {
      const { error: itemsErr } = await supabase
        .from("saved_look_items")
        .delete()
        .eq("look_id", id);
      if (itemsErr) throw itemsErr;
      const { error } = await supabase.from("saved_looks").delete().eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      toast.success("Look deleted");
      qc.invalidateQueries({ queryKey: ["saved_looks", uid] });
      qc.invalidateQueries({ queryKey: ["saved_looks_favorites", uid] });
      navigate({ to: "/looks" });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateRating = useMutation({
    mutationFn: async (rating: number) => {
      const { error } = await supabase
        .from("saved_looks")
        .update({ rating } as never)
        .eq("id", id);
      if (error) throw new Error(error.message || "Network error");
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["saved_look_detail", id] });
      qc.invalidateQueries({ queryKey: ["saved_looks", uid] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const onShare = async () => {
    if (!data) return;
    // Attempt canvas card share for visual look sharing
    try {
      const canvas = document.createElement("canvas");
      const W = 1080;
      const H = 1350;
      canvas.width = W;
      canvas.height = H;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.fillStyle = "#F8F5FA";
        ctx.fillRect(0, 0, W, H);
        if (data.look_photo_url) {
          const { data: signed } = await supabase.storage
            .from("collection-photos")
            .createSignedUrl(data.look_photo_url, 60);
          if (signed?.signedUrl) {
            await new Promise<void>((resolve) => {
              const img = new Image();
              img.crossOrigin = "anonymous";
              img.onload = () => {
                const heroH = Math.round(H * 0.62);
                ctx.drawImage(img, 0, 0, W, heroH);
                const grad = ctx.createLinearGradient(0, heroH - 160, 0, heroH);
                grad.addColorStop(0, "rgba(248,245,250,0)");
                grad.addColorStop(1, "rgba(248,245,250,1)");
                ctx.fillStyle = grad;
                ctx.fillRect(0, heroH - 160, W, 160);
                resolve();
              };
              img.onerror = () => resolve();
              img.src = signed.signedUrl;
            });
          }
        }
        ctx.fillStyle = "#1E1624";
        ctx.font = "italic 52px Georgia, serif";
        ctx.textAlign = "center";
        ctx.fillText(data.name, W / 2, 880);
        if (data.occasion) {
          ctx.fillStyle = "#6B5E6E";
          ctx.font = "28px Arial, sans-serif";
          ctx.fillText(data.occasion.toUpperCase(), W / 2, 935);
        }
        const pieceNames = data.pieces.slice(0, 4).map((p) => p.name);
        ctx.fillStyle = "#9B7FA8";
        ctx.font = "italic 28px Georgia, serif";
        let pieceY = 1000;
        for (const name of pieceNames) {
          ctx.fillText(name, W / 2, pieceY);
          pieceY += 46;
        }
        ctx.fillStyle = "#C6A66B";
        ctx.font = "32px Georgia, serif";
        ctx.fillText("CAILÈVE", W / 2, H - 80);
        ctx.fillStyle = "#6B5E6E";
        ctx.font = "22px Arial, sans-serif";
        ctx.fillText("caileve.app", W / 2, H - 44);
        const blob = await new Promise<Blob | null>((res) =>
          canvas.toBlob(res, "image/png")
        );
        if (blob && navigator.share && navigator.canShare) {
          const file = new File([blob], "my-look.png", { type: "image/png" });
          if (navigator.canShare({ files: [file] })) {
            await navigator.share({ files: [file], title: data.name, text: "caileve.app" });
            return;
          }
        }
      }
    } catch {
      // Canvas share failed — fall through to text share
    }

    const lines = data.pieces.map((p) => `• ${p.name}`).join("\n");
    const text = `${data.name}${data.occasion ? ` — ${data.occasion}` : ""}\n\n${lines}\n\ncaileve.app`;
    try {
      if (navigator.share) {
        await navigator.share({ title: data.name, text });
      } else {
        await navigator.clipboard.writeText(text);
        toast.success("Look details copied to clipboard");
      }
    } catch {
      /* user dismissed */
    }
  };

  if (isLoading) {
    return <p className="max-w-3xl mx-auto px-8 py-16 text-[var(--caileve-lilac-text)]">Loading…</p>;
  }
  if (!data) {
    return (
      <p className="max-w-3xl mx-auto px-8 py-16 font-serif-display text-[var(--caileve-ink)]">
        Look not found.
      </p>
    );
  }

  const moodItems: MoodBoardItem[] = data.pieces.map((p) => ({
    type: p.kind,
    id: p.id,
    name: p.name,
    photo_url: p.photo_url,
    photo_url_no_bg: p.photo_url_no_bg,
    prefer_clean_bg: p.prefer_clean_bg,
    clean_bg_color: p.clean_bg_color,
    missing: p.missing,
  }));
  const presentPieces = data.pieces.filter((p) => !p.missing);
  const hasMissing = data.pieces.some((p) => p.missing);
  const clothingCount = presentPieces.filter((p) => p.kind === "clothing").length;
  const jewelryCount = presentPieces.filter((p) => p.kind === "jewelry").length;
  const accessoryCount = presentPieces.filter((p) => p.kind === "accessory").length;

  return (
    <div className="max-w-4xl mx-auto px-8 py-10" style={{ color: "var(--caileve-ink)" }}>
      <div className="flex items-center justify-between mb-8">
        <Link
          to="/looks"
          className="inline-flex items-center text-[11px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] hover:text-[var(--caileve-ink)]"
        >
          <ChevronLeft className="h-3 w-3 mr-1" /> All looks
        </Link>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onShare}
            aria-label="Share look"
            className="h-9 w-9 flex items-center justify-center rounded-full border border-[var(--caileve-soft)] text-[var(--caileve-lilac)] hover:bg-[var(--caileve-soft)]"
          >
            <Share2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label="Edit look"
            className="h-9 w-9 flex items-center justify-center rounded-full border border-[var(--caileve-soft)] text-[var(--caileve-lilac)] hover:bg-[var(--caileve-soft)]"
          >
            <Pencil className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <button
            type="button"
            onClick={() => setConfirmDel(true)}
            aria-label="Delete look"
            className="h-9 w-9 flex items-center justify-center rounded-full border border-[var(--caileve-soft)] text-[var(--caileve-copper)] hover:bg-[var(--caileve-soft)]"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      <header className="mb-10">
        <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)]">Saved Look</p>
        <h1
          className="font-serif-display text-5xl md:text-6xl mt-3 leading-[1.05]"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}
        >
          {data.name}
        </h1>
        <div className="flex flex-wrap items-center gap-4 mt-4">
          {data.occasion && (
            <p className="font-serif-display italic text-xl text-[var(--caileve-lilac)]">{data.occasion}</p>
          )}
          <StarRating
            value={data.rating ?? 0}
            onChange={(n) => updateRating.mutate(n)}
            size="md"
          />
        </div>
        {((data.feelings && data.feelings.trim().length > 0) || (data.mood && data.mood.trim().length > 0)) && (
          <div className="mt-6">
            <p
              className="text-xs tracking-[0.05em]"
              style={{
                fontFamily: "'Cormorant Garamond', Georgia, serif",
                color: "var(--caileve-lilac-text)",
                fontSize: "0.95rem",
              }}
            >
              How this look makes you feel
            </p>
            <div className="flex flex-wrap gap-2 mt-2">
              {(data.feelings ?? "")
                .split(",")
                .map((f) => f.trim())
                .filter(Boolean)
                .map((f) => (
                  <span
                    key={f}
                    className="px-3 py-1 rounded-full text-sm"
                    style={{
                      backgroundColor: "var(--caileve-copper)",
                      color: "var(--caileve-bg)",
                      fontFamily: "'Cormorant Garamond', Georgia, serif",
                    }}
                  >
                    {f}
                  </span>
                ))}
              {data.mood && data.mood.trim().length > 0 && (
                <span
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: "transparent",
                    color: "var(--caileve-copper)",
                    border: "1px solid var(--caileve-copper)",
                    fontFamily: "'Cormorant Garamond', Georgia, serif",
                  }}
                >
                  {data.mood}
                </span>
              )}
            </div>
          </div>
        )}
      </header>

      <section>
        {/* Orbit Board — piece composition always shown */}
        {moodItems.length > 0 ? (
          <div style={{ marginLeft: -24, marginRight: -24 }}>
            <CaileveOrbitBoard items={moodItems} altName={data.name} />
          </div>
        ) : (
          <div
            className="aspect-[4/5] flex items-center justify-center"
            style={{ backgroundColor: "var(--caileve-soft)" }}
          >
            <p className="font-serif-display italic text-xl text-[var(--caileve-lilac)]">
              A composed look awaits.
            </p>
          </div>
        )}

        {/* Worn photo — shown below the orbit board when a look photo exists.
            The orbit board shows the piece composition; this shows the worn moment.
            The two are deliberately separate: composition and memory. */}
        {data.look_photo_url && (
          <div className="mt-8">
            <p
              className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)] mb-3"
            >
              Worn photo
            </p>
            <ImageBox
              src={data.look_photo_url}
              alt={data.name}
              aspect="aspect-[4/5]"
            />
          </div>
        )}
      </section>

      {showShareNudge && (
        <button
          type="button"
          onClick={() => share()}
          className="block mt-4 text-sm italic hover:opacity-80 transition-opacity"
          style={{
            color: "var(--caileve-muted)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
        >
          Love this look? Share Cailève with a friend.
        </button>
      )}

      {hasMissing && (
        <p
          className="mt-3 italic text-sm"
          style={{
            color: "var(--caileve-muted)",
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
        >
          One or more pieces in this look have been removed from your collection.
        </p>
      )}

      {data.styling_notes && (
        <section className="mt-12">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)]">Your Notes</p>
          <p
            className="mt-4 text-xl leading-relaxed"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}
          >
            {data.styling_notes}
          </p>
        </section>
      )}

      {presentPieces.length > 0 && (
        <section className="mt-14">
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--caileve-lilac-text)]">
            In this look
            {data.look_photo_url && clothingCount === 0
              ? " · scanned piece"
              : ` · ${clothingCount} clothing`}
            {` · ${jewelryCount} jewelry`}
            {` · ${accessoryCount} accessor${accessoryCount === 1 ? "y" : "ies"}`}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-5">
            {presentPieces.map((p) => {
              const to =
                p.kind === "clothing"
                  ? "/clothing/$id"
                  : p.kind === "accessory"
                    ? "/accessories/$id"
                    : "/jewelry/$id";
              const fallbackLabel =
                p.kind === "clothing" ? "Clothing" : p.kind === "accessory" ? "Accessory" : "Jewelry";
              return (
                <Link
                  key={`${p.kind}-${p.id}`}
                  to={to}
                  params={{ id: p.id }}
                  className="group block bg-[var(--caileve-bg)] border border-[var(--caileve-soft)] p-3 hover:border-[var(--caileve-lilac)] transition-colors"
                >
                  <ImageBox src={p.photo_url} alt={p.name} aspect="aspect-square" />
                  <p
                    className="mt-3 text-base leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", color: "var(--caileve-ink)" }}
                  >
                    {p.name}
                  </p>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-[var(--caileve-lilac-text)] mt-1">
                    {p.caption ?? fallbackLabel}
                  </p>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      <Dialog open={editing} onOpenChange={(o) => !o && setEditing(false)}>
        {editing && (
          <LookEditDialog
            lookId={id}
            userId={uid}
            onDone={() => {
              setEditing(false);
              qc.invalidateQueries({ queryKey: ["saved_look_detail", id] });
            }}
          />
        )}
      </Dialog>

      <AlertDialog open={confirmDel} onOpenChange={setConfirmDel}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this look?</AlertDialogTitle>
            <AlertDialogDescription>
              "{data.name}" will be permanently removed from your lookbook. The clothing and jewelry pieces themselves will remain in your collection.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={del.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={del.isPending}
              onClick={(e) => {
                e.preventDefault();
                del.mutate();
              }}
            >
              {del.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}